/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import classNames from 'classnames';
import type React from 'react';

import {
  type CommonProps,
  type PolymorphicCommonProps,
} from '../_common/types';
import { CleanIconButton } from '../clean-icon-button';
import { BoltIconOutline } from '../icons/generated/heroIcons/BoltIconOutline';
import { CheckCircleIconSolid } from '../icons/generated/heroIcons/CheckCircleIconSolid';
import { ExclamationCircleIconSolid } from '../icons/generated/heroIcons/ExclamationCircleIconSolid';
import { ExclamationTriangleIconSolid } from '../icons/generated/heroIcons/ExclamationTriangleIconSolid';
import { InformationCircleIconSolid } from '../icons/generated/heroIcons/InformationCircleIconSolid';
import { XMarkIconOutline } from '../icons/generated/heroIcons/XMarkIconOutline';
import { Typography } from '../typography';
/**
 *
 *
 * Types
 *
 *
 */

type BannerCommonProps = {
  /** Shows a status icon. The icon is based on the value of the `variant` prop */
  hasIcon?: boolean;

  /** Content to be shown in the banner. */
  children?: React.ReactNode;

  /** Sets the role to alert */
  isAlert?: boolean;

  /** How the banner is used. */
  usage?: 'inline' | 'global';

  /** The variant of the banner */
  variant?: BannerVariant;
};

type CloseableBannerProps = {
  /** If the banner is closeable. Renders an icon button in the top right corner */
  isCloseable: true;

  /** Event handler for when the close icon is clicked */
  onClose: (
    event?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>,
  ) => void;
};

type NonCloseableBannerProps = {
  isCloseable?: false;
  onClose?: never;
};

type BannerCloseProps = CloseableBannerProps | NonCloseableBannerProps;

type BannerProps = BannerCommonProps & BannerCloseProps;
type BannerVariant = 'info' | 'success' | 'warning' | 'danger' | 'neutral';

const StatusIcon = ({ variant }: { variant: BannerVariant }) => {
  const iconComponent = (() => {
    switch (variant) {
      case 'success':
        return <CheckCircleIconSolid />;
      case 'warning':
        return <ExclamationTriangleIconSolid />;
      case 'danger':
        return <ExclamationCircleIconSolid />;
      case 'info':
        return <InformationCircleIconSolid />;
      case 'neutral':
        return <BoltIconOutline />;
      default:
        return null;
    }
  })();

  return <div className="ndl-banner-status-icon">{iconComponent}</div>;
};

const BannerHeader = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'span', { children: React.ReactNode }>) => {
  const classes = classNames('ndl-banner-header', className);
  return (
    <Typography
      as="span"
      variant="title-4"
      className={classes}
      style={style}
      ref={ref}
      htmlAttributes={htmlAttributes}
      {...restProps}
    >
      {children}
    </Typography>
  );
};

const BannerDescription = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', { children: React.ReactNode }>) => {
  const classes = classNames('ndl-banner-description', className);
  return (
    <Typography
      as="div"
      variant="body-medium"
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </Typography>
  );
};

const BannerActions = ({
  children,
  className,
  style,
  htmlAttributes,
  ref,
  ...restProps
}: CommonProps<'div', { children: React.ReactNode }>) => {
  const classes = classNames('ndl-banner-actions', className);
  return (
    <div
      className={classes}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {children}
    </div>
  );
};

const BannerComponent = <T extends React.ElementType = 'section'>({
  as,
  variant = 'info',
  hasIcon = false,
  onClose,
  className,
  children,
  isCloseable = false,
  usage = 'inline',
  htmlAttributes,
  isAlert = false,
  ref,
  ...restProps
}: PolymorphicCommonProps<T, BannerProps>) => {
  const Element: React.ElementType = as ?? 'section';

  const classes = classNames('ndl-banner', className, {
    'ndl-danger': variant === 'danger',
    'ndl-global': usage === 'global',
    'ndl-info': variant === 'info',
    'ndl-inline': usage === 'inline',
    'ndl-neutral': variant === 'neutral',
    'ndl-success': variant === 'success',
    'ndl-warning': variant === 'warning',
  });

  const handleClose = (
    event:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>,
  ) => {
    if (onClose && typeof onClose === 'function') {
      onClose(event);
    }
  };

  return (
    <Element
      className={classes}
      role={isAlert ? 'alert' : undefined}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {hasIcon && <StatusIcon variant={variant} />}
      <div className="ndl-banner-content">{children}</div>
      {isCloseable && (
        <CleanIconButton
          size="small"
          onClick={handleClose}
          description={null}
          htmlAttributes={{ 'aria-label': 'Close' }}
        >
          <XMarkIconOutline />
        </CleanIconButton>
      )}
    </Element>
  );
};

const Banner = Object.assign(BannerComponent, {
  Actions: BannerActions,
  Description: BannerDescription,
  Header: BannerHeader,
});

export { Banner };
